import { ClientOrganizerScheduleOptions, SDKOperationFor, SDKRequestFor } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { formatISO8601Date } from '@st/util/time'
import { SdkClient } from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'
import { reportHandledError } from '../error-reporting/report-handled-error'

export const LAST_ALLOWED_END_DATE = '2026-10-15'

export const DEFAULT_SCHEDULE_OPTIONS: ClientOrganizerScheduleOptions = {
  firstDate: '2026-02-10',
  lastDate: '2026-04-15',
  timeZone: 'America/New_York',
  daysOfWeek: ['monday'],
  timesOfDay: ['09:00']
}

export type IdleState = { status: 'idle' }

export type BulkEnrollState =
  | IdleState
  | OpeningState
  | EditTemplateState
  | SelectScheduleState
  | ConfirmEnrollState

export type OpeningState = {
  status: 'opening'
  organizationId: string
  folderIds: string[]
}

export type EditTemplateState = {
  status: 'editTemplate'
  today: string
  organizationId: string
  folderIds: string[]
}

export type SelectScheduleState = {
  status: 'selectSchedule'
  today: string
  scheduleOptions?: ClientOrganizerScheduleOptions
  organizationId: string
  folderIds: string[]
  validationError?: ScheduleValidationError
}

export type ConfirmEnrollState = {
  status: 'confirm'
  today: string
  scheduleOptions: ClientOrganizerScheduleOptions
  organizationId: string
  folderIds: string[]
  operationStatus: 'idle' | 'enrolling' | 'enrolled'
}

export type ScheduleValidationError = { type: 'invalidDateRange'; message: string }

type SDKResponse = {
  type: 'response'
  operation: {
    type: SDKOperationFor<'folders/bulkEnrollClientOrganizersInReminders'>['type']
    request: SDKRequestFor<'folders/bulkEnrollClientOrganizersInReminders'>
    response: SDKOperationFor<'folders/bulkEnrollClientOrganizersInReminders'>['response']
  }
}

type SDKRequestFailed = {
  type: 'requestFailed'
}

export type BulkEnrollMessage =
  | { type: 'opened'; today: string }
  | {
      type: 'continueToSelectSchedule'
    }
  | {
      type: 'backToEditTemplate'
    }
  | {
      type: 'continueToConfirm'
      scheduleOptions: ClientOrganizerScheduleOptions
    }
  | { type: 'confirmEnroll' }
  | SDKResponse
  | SDKRequestFailed

export function idleBulkEnroll(): BulkEnrollState {
  return { status: 'idle' }
}

export function openBulkEnroll(args: {
  organizationId: string
  folderIds: string[]
}): BulkEnrollState {
  return {
    status: 'opening',
    organizationId: args.organizationId,
    folderIds: args.folderIds
  }
}

/**
 * This chart owns one reminder-enrollment attempt after the folder-list parent opens it.
 * The parent owns route lifetime, declares the date Task, and launches the accepted enrollment
 * Command. This chart owns only the domain state and transitions.
 */
export function updateBulkEnroll(
  state: BulkEnrollState,
  message: BulkEnrollMessage
): BulkEnrollState {
  return match({ state, message })
    .returnType<BulkEnrollState>()
    .with({ state: { status: 'opening' }, message: { type: 'opened' } }, ({ state, message }) => {
      return {
        ...state,
        status: 'editTemplate',
        today: message.today
      } satisfies EditTemplateState
    })
    .with(
      { state: { status: 'editTemplate' }, message: { type: 'continueToSelectSchedule' } },
      ({ state }) => {
        return {
          status: 'selectSchedule',
          organizationId: state.organizationId,
          folderIds: state.folderIds,
          today: state.today
        } satisfies SelectScheduleState
      }
    )
    .with(
      { state: { status: 'selectSchedule' }, message: { type: 'backToEditTemplate' } },
      ({ state }) => {
        return {
          status: 'editTemplate',
          organizationId: state.organizationId,
          folderIds: state.folderIds,
          today: state.today
        } satisfies EditTemplateState
      }
    )
    .with(
      { state: { status: 'selectSchedule' }, message: { type: 'continueToConfirm' } },
      ({ state, message }) => {
        const validationError = validateSchedule(state, message.scheduleOptions)
        if (validationError) {
          return {
            ...state,
            validationError
          } satisfies SelectScheduleState
        }
        return {
          status: 'confirm',
          organizationId: state.organizationId,
          folderIds: state.folderIds,
          scheduleOptions: message.scheduleOptions,
          operationStatus: 'idle',
          today: state.today
        } satisfies ConfirmEnrollState
      }
    )
    .with(
      {
        state: { status: 'confirm', operationStatus: 'idle' },
        message: { type: 'confirmEnroll' }
      },
      ({ state }) => {
        return { ...state, operationStatus: 'enrolling' } satisfies ConfirmEnrollState
      }
    )
    .with(
      {
        state: { status: 'confirm', operationStatus: 'enrolling' },
        message: {
          type: 'response',
          operation: { type: 'folders/bulkEnrollClientOrganizersInReminders' }
        }
      },
      ({ state }) => ({ ...state, operationStatus: 'enrolled' })
    )
    .with(
      {
        state: { status: 'confirm', operationStatus: 'enrolling' },
        message: { type: 'requestFailed' }
      },
      ({ state }) => ({ ...state, operationStatus: 'idle' })
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function unexpectedMessage(state: BulkEnrollState, message: BulkEnrollMessage): never {
  throw new Error(`Unexpected reminder enrollment message ${message.type} while ${state.status}`)
}

export type BulkEnrollInput = Pick<
  ConfirmEnrollState,
  'organizationId' | 'folderIds' | 'scheduleOptions'
>

export function sendEnrollRequestEffect(state: BulkEnrollInput) {
  const request: SDKRequestFor<'folders/bulkEnrollClientOrganizersInReminders'> = {
    type: 'folders/bulkEnrollClientOrganizersInReminders',
    organizationId: state.organizationId,
    folderIds: state.folderIds,
    scheduleOptions: state.scheduleOptions
  }

  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(sdk.send(request), {
      onError: (error) =>
        reportHandledError(error, {
          source: 'st-folder-list',
          operation: request.type,
          extra: { request }
        })
    })

    if (result.ok) {
      const operation: SDKOperationFor<'folders/bulkEnrollClientOrganizersInReminders'> = {
        type: request.type,
        request,
        response: result.response
      }
      return {
        type: 'response',
        operation
      } as const
    }

    return { type: 'requestFailed' } as const
  })
}

function validateSchedule(
  state: SelectScheduleState,
  scheduleOptions: ClientOrganizerScheduleOptions
): ScheduleValidationError | undefined {
  if (scheduleOptions.firstDate < state.today) {
    return { type: 'invalidDateRange', message: 'Starting date cannot be in the past' }
  } else if (scheduleOptions.lastDate > LAST_ALLOWED_END_DATE) {
    return {
      type: 'invalidDateRange',
      message: 'Ending date cannot be after ' + formatISO8601Date(LAST_ALLOWED_END_DATE)
    }
  } else if (scheduleOptions.firstDate > scheduleOptions.lastDate) {
    return { type: 'invalidDateRange', message: 'Starting date cannot be after ending date' }
  }
  return undefined
}
